import React from 'react';
const { useState, useEffect, useRef } = React;
import { APTITUDES, ENERGY_OPTIONS, CHANNELS, MOTIVATORS, ROLES, t } from '../../diagnosticConfig.js';
import { adminCall, cardSt, labelSt, mutedSt, badgeSt, warnSt, fmtDate } from './shared.jsx';
import { AptitudeValidation, DimensionValidation } from './AiValidation.jsx';
import MethodUsedNote, { useMethodTitles, MethodLibraryRecord } from '../MethodUsedNote.jsx';
import ClientContextCard from './ClientContext.jsx';

// IA: the AI pipeline of the Diagnostic. Per person P1 (evidence) → Ale approves →
// P2 (levels) → Ale validates → P3 seller / founder (read-only drafts). For the
// team P4 (dimensions) → Ale validates → P5 (report draft). Each run happens in
// a background function; this panel polls aiStatus while something is running.

const POLL_MS = 4000;

export const GATE_LABELS = {
  ai_disabled: 'La IA solo corre en diagnósticos de prueba hasta que DIAGNOSTIC_AI_REAL_CLIENTS sea "true".',
  running: 'Ya está corriendo.',
  self_not_submitted: 'Falta que envíe la autoevaluación.',
  p1_not_approved: 'Primero aprueba la evidencia (P1).',
  p2_not_approved: 'Primero valida y aprueba los niveles (P2).',
  team_p2_pending: 'Falta aprobar los niveles (P2) de todas las personas.',
  p4_not_approved: 'Primero valida y aprueba las dimensiones (P4).',
  no_people: 'No hay personas evaluadas.',
  not_evaluated: 'Esta persona no tiene autoevaluación.',
  trigger_failed: 'No se pudo iniciar la corrida. Vuelve a intentarlo.',
  published: 'Tiene un link publicado: revócalo en Entregables antes de volver a correrlo.',
  closed: 'El diagnóstico está cerrado.',
  client_context_missing: 'Falta el contexto del cliente (industria, tamaño, tipo de venta o país): complétalo arriba.',
};
const ERROR_KINDS = {
  format: 'La respuesta de la IA no tuvo el formato esperado, tampoco en el reintento.',
  service: 'El servicio de IA no respondió.',
  inputs: 'Faltan datos para este paso.',
  interrupted: 'La corrida se interrumpió.',
  disabled: 'Bloqueada por el interruptor de seguridad.',
  airtable: 'Airtable rechazó una lectura o escritura.',
  internal: 'Error interno.',
};
export const STATUS = {
  running: ['⏳ Corriendo', '#2D5F99'], draft: ['Borrador', '#9A6212'], approved: ['✓ Aprobado', 'var(--globant-green)'],
  failed: ['✗ Falló', '#B4362C'], interrupted: ['✗ Interrumpida', '#B4362C'], superseded: ['Reemplazada', 'var(--globant-muted)'],
};
const STEP_TITLES = {
  P1: 'P1 · Evidencia', P2: 'P2 · Niveles', P3seller: 'P3 · Ficha vendedor', P3founder: 'P3 · Ficha founder',
  P4: 'P4 · Dimensiones', P5: 'P5 · Informe del founder',
};
const SOURCE_LABELS = {
  self_assessment: 'Autoevaluación', exercise_1: 'Ejercicio 1', exercise_2: 'Ejercicio 2', real_material: 'Material real',
  roleplay: 'Role-play', crm: 'CRM', transcript: 'Transcripción', ale_notes: 'Notas de Ale',
};

const anyRunning = (s) => !!s && [...s.people.flatMap(p => Object.values(p.slots)), ...Object.values(s.team)].some(x => x.current?.status === 'running');

export function StatusBadge({ status }) {
  const [label, color] = STATUS[status] || [status, 'var(--globant-muted)'];
  return <span style={{ ...badgeSt(false), color }}>{label}</span>;
}

/** Scored with which rubric (P2, P4): an output from before versions were recorded shows "no registrada". */
export function RubricTag({ output, current }) {
  if (!output || !('rubricVersion' in output) || output.status === 'running') return null;
  const v = output.rubricVersion;
  const other = v && current && v !== current;
  return (
    <span style={{ ...badgeSt(false), ...(other || !v ? { color: '#9A6212' } : {}) }}
      title={v ? `Puntuado con la rúbrica v${v}${other ? `; la vigente es v${current}` : ''}` : 'Esta salida es de antes de registrar la versión de la rúbrica'}>
      Rúbrica {v ? `v${v}` : 'no registrada'}
    </span>
  );
}

/** Warning when the scores of a diagnostic were made with different rubric versions. */
export function RubricWarning({ rubric }) {
  if (!rubric || (!rubric.mixed && !rubric.outdated)) return null;
  const label = (x) => `${x.step}${x.name ? ` de ${x.name}` : ''}: ${x.version ? `v${x.version}` : 'no registrada'}`;
  return (
    <div style={warnSt}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>⚠️ Versiones de la rúbrica</div>
      <div style={{ fontSize: 13 }}>
        {rubric.mixed
          ? 'Los puntajes de este diagnóstico se hicieron con versiones distintas de la rúbrica: no se pueden comparar uno a uno. '
          : ''}
        {rubric.outdated ? `La versión vigente es v${rubric.current}; volver a correr P2 o P4 los puntúa con ella. ` : ''}
      </div>
      <div style={{ ...mutedSt, marginTop: 4 }}>{rubric.scored.map(label).join(' · ')}</div>
    </div>
  );
}

// The steps that get the Method Library (P3, P5): their outputs always show the indicator
const isMethodStep = (title) => /^P[35] /.test(title);

function Slot({ title, slot, busy, onRun, onOpen, selected, rubricCurrent, titles }) {
  const cur = slot.current;
  const running = cur?.status === 'running';
  return (
    <div style={{ border: `1px solid ${selected ? 'var(--globant-green)' : 'var(--globant-border)'}`, borderRadius: 8, padding: 8, minWidth: 170, flex: '1 1 170px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        {cur ? <><StatusBadge status={cur.status} /><span style={mutedSt}>v{cur.version}</span></> : <span style={mutedSt}>Sin correr</span>}
        {checkWarnings(cur?.checks).length > 0 && <span style={{ ...badgeSt(false), color: '#9A6212' }}>⚠️ {checkWarnings(cur.checks).length}</span>}
        {cur?.stale && <span style={{ ...badgeSt(false), color: '#9A6212' }} title="Se generó con una versión anterior del paso previo">Desactualizado</span>}
        <RubricTag output={cur} current={rubricCurrent} />
      </div>
      {cur && cur.status !== 'running' && <MethodUsedNote method={cur.methodLibrary} titles={titles} expected={isMethodStep(title)} style={{ marginTop: 0, marginBottom: 6 }} />}
      {slot.lastFailed && <div style={{ fontSize: 11, color: '#B4362C', marginBottom: 6 }}>v{slot.lastFailed.version}: {ERROR_KINDS[slot.lastFailed.errorKind] || ERROR_KINDS.internal}
        {slot.lastFailed.errorDetail && <div style={{ fontFamily: 'monospace', fontSize: 10, marginTop: 2, wordBreak: 'break-all' }} title="Detalle de Airtable (solo tipo y campo, sin valores)">{slot.lastFailed.errorDetail}</div>}</div>}
      {slot.gate && slot.gate !== 'running' && <div style={{ ...mutedSt, fontSize: 11, marginBottom: 6 }}>{GATE_LABELS[slot.gate] || slot.gate}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        {cur && !running && <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => onOpen(cur.id)}>Ver</button>}
        <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} disabled={busy || !!slot.gate}
          onClick={() => onRun(cur)}>{cur ? '↻ Volver a correr' : '▶ Correr'}</button>
      </div>
    </div>
  );
}

export default function AiPipeline({ diagnosticId, run, busy }) {
  const [status, setStatus] = useState(null);
  const [output, setOutput] = useState(null);
  const timer = useRef(null);
  const titles = useMethodTitles();

  const load = () => run(async () => setStatus(await adminCall({ action: 'aiStatus', diagnosticId })));
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [diagnosticId]);

  // Poll quietly while something runs; refresh the open output when its run ends.
  useEffect(() => {
    clearTimeout(timer.current);
    if (!anyRunning(status)) return;
    timer.current = setTimeout(async () => {
      try { setStatus(await adminCall({ action: 'aiStatus', diagnosticId })); } catch { setStatus(s => ({ ...s })); /* retry on the next tick */ }
    }, POLL_MS);
  }, [status]);

  const open = (outputId) => run(async () => setOutput(await adminCall({ action: 'getOutput', outputId })));
  const start = (step, current, participantId = null, variant = null) => {
    if (current && !window.confirm('Volver a correr crea una versión nueva. La actual queda como reemplazada cuando termine bien. ¿Seguimos?')) return;
    run(async () => setStatus(await adminCall({ action: 'aiRun', diagnosticId, step, participantId, variant })));
  };
  const act = (body) => run(async () => {
    setOutput(await adminCall(body));
    setStatus(await adminCall({ action: 'aiStatus', diagnosticId }));
  });

  if (!status) return null;
  const sel = output?.id;
  const slotSelected = (slot) => !!slot.current && slot.current.id === sel;

  return (
    <div>
      {!status.ai.enabled && (
        <div style={warnSt}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>🔒 IA bloqueada para este diagnóstico</div>
          <div style={{ fontSize: 13 }}>Hasta firmar el acuerdo de tratamiento de datos, la IA solo corre en diagnósticos de prueba. Para habilitarla con clientes reales, la variable de entorno DIAGNOSTIC_AI_REAL_CLIENTS tiene que ser "true".</div>
        </div>
      )}

      <RubricWarning rubric={status.rubric} />

      <ClientContextCard diagnosticId={diagnosticId} run={run} busy={busy} />

      <div style={cardSt}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={labelSt}>Personas</div>
          <span style={mutedSt}>Modelo: {status.ai.model} · <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={load} disabled={busy}>↻ Actualizar</button></span>
        </div>
        {!status.people.length && <div style={mutedSt}>Todavía no hay personas con autoevaluación.</div>}
        {status.people.map(p => (
          <div key={p.id} style={{ borderBottom: '1px solid var(--globant-border)', padding: '10px 0' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{p.name} <span style={mutedSt}>· {t(ROLES[p.role]?.label) || p.role}</span></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['P1', 'P1'], ['P2', 'P2'], ['P3seller', 'P3', 'seller'], ['P3founder', 'P3', 'founder']].map(([k, step, variant]) => (
                <Slot key={k} title={STEP_TITLES[k]} slot={p.slots[k]} busy={busy} selected={slotSelected(p.slots[k])} rubricCurrent={status.rubric?.current} titles={titles}
                  onOpen={open} onRun={(cur) => start(step, cur, p.id, variant || null)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 10 }}>Equipo</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['P4', 'P5'].map(k => (
            <Slot key={k} title={STEP_TITLES[k]} slot={status.team[k]} busy={busy} selected={slotSelected(status.team[k])}
              onOpen={open} onRun={(cur) => start(k, cur)} />
          ))}
        </div>
      </div>

      {output && <OutputDetail key={`${output.id}:${output.status}`} output={output} busy={busy} act={act} titles={titles} onClose={() => setOutput(null)} />}
    </div>
  );
}

function OutputDetail({ output, busy, act, titles, onClose }) {
  const title = STEP_TITLES[output.step === 'P3' ? `P3${output.variant}` : output.step];
  const canReopen = output.status === 'approved' && output.isCurrent && ['P1', 'P2', 'P4'].includes(output.step);
  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{title}{output.person ? ` · ${output.person.name}` : ''} <span style={mutedSt}>v{output.version}</span></div>
          <div style={{ ...mutedSt, marginTop: 4 }}>
            <StatusBadge status={output.status} /> {output.model} · {fmtDate(output.finishedAt || output.created, true)}
            {output.approvedAt && ` · Aprobado por ${output.approvedBy || '—'} el ${fmtDate(output.approvedAt, true)}`}
            {!output.isCurrent && ' · Versión anterior'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {canReopen && <button className="action-btn" style={{ fontSize: 12 }} disabled={busy}
            onClick={() => window.confirm('Vuelve a borrador para corregir la validación. ¿Seguimos?') && act({ action: 'reopenOutput', outputId: output.id })}>↺ Reabrir</button>}
          <button className="action-btn" style={{ fontSize: 12 }} onClick={onClose}>Cerrar</button>
        </div>
      </div>
      {output.stale && <div style={{ ...warnSt, fontSize: 13 }}>Se generó con una versión anterior del paso previo. Conviene volver a correrlo.</div>}
      {['P3', 'P5'].includes(output.step) && <MethodLibraryRecord output={output} titles={titles} />}
      <Checks checks={output.checks} refs={output.refs} step={output.step} variant={output.variant} />

      {output.step === 'P1' && <Evidence content={output.content} />}
      {output.step === 'P1' && output.status === 'draft' && output.isCurrent && (
        <div style={{ textAlign: 'right', marginTop: 12 }}>
          <button className="action-btn btn-primary" disabled={busy} onClick={() => act({ action: 'approveEvidence', outputId: output.id })}>✓ Aprobar evidencia</button>
        </div>
      )}
      {output.step === 'P2' && <AptitudeValidation output={output} busy={busy} onSave={act} />}
      {output.step === 'P4' && <DimensionValidation output={output} busy={busy} onSave={act} />}
      {['P3', 'P5'].includes(output.step) && (
        <>
          <div style={{ ...mutedSt, marginBottom: 8 }}>Para editar, aprobar y publicar este entregable, usa la pestaña Entregables.</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, background: 'rgba(30,42,44,0.03)', borderRadius: 8, padding: 12 }}>{output.content}</div>
        </>
      )}
    </div>
  );
}

// Automatic checks after each run (diagnosticChecks.ts): warnings, never blocking.
export function checkWarnings(checks) {
  if (!checks) return [];
  const w = [];
  if (checks.verbatimMatches > 0) w.push(`El borrador repite ${checks.verbatimMatches} tramos de 6 o más palabras de la 1:1 o de la Parte 3. La versión founder no puede llevar frases textuales.`);
  if (checks.motivatorsWithoutConsent > 0) w.push(`Habla de motivadores sin consentimiento en ${checks.motivatorsWithoutConsent} frases (versión founder sin consentimiento, o una persona del informe que no lo dio).`);
  if (checks.placeholders > 0) w.push(`Hay ${checks.placeholders} placeholders entre corchetes (por ejemplo, [Tu Nombre]).`);
  if (checks.codeBlocks > 0) w.push(`Hay ${checks.codeBlocks} bloques de código dentro del texto.`);
  if (checks.englishSentences > 0) w.push(`Hay ${checks.englishSentences} frases que parecen estar en inglés.`);
  if (checks.strengthsOutsideZone?.length) w.push(`Fortalezas fuera de zona Fortaleza o Sólido: ${checks.strengthsOutsideZone.join(', ')}.`);
  const k = checks.cleaned || {};
  const list = (x) => x.join(', ');
  if (k.selfAssessmentOnly?.length) w.push(`Se quitó evidencia que solo venía de la autoevaluación en: ${list(k.selfAssessmentOnly)}.`);
  if (k.aptitudesNotApplicable?.length) w.push(`Se quitaron aptitudes que no aplican a la persona: ${list(k.aptitudesNotApplicable)}.`);
  if (k.duplicateAptitudes?.length) w.push(`Se quitaron aptitudes repetidas: ${list(k.duplicateAptitudes)}.`);
  if (k.motivatorsNotInTop3 > 0) w.push(`Se quitaron ${k.motivatorsNotInTop3} motivadores que no están en el top 3 de la persona.`);
  if (k.energyNotSpecific?.length) w.push(`Se quitó la energía de ${list(k.energyNotSpecific)}: la misma frase general (cansancio o frustración, sin nombrar una actividad) estaba asignada a varias aptitudes. Quedó como señal general.`);
  if (k.exitRiskWithoutIntent > 0) w.push(`${k.exitRiskWithoutIntent} "señales de riesgo" no expresaban duda ni intención de irse: se pasaron a "Qué necesita para quedarse".`);
  if (k.duplicateMotivators > 0) w.push(`Se quitaron ${k.duplicateMotivators} motivadores repetidos.`);
  if (k.repeatedQuotes > 0) w.push(`Se quitaron ${k.repeatedQuotes} citas repetidas entre motivadores.`);
  if (k.strengthsTrimmed || k.gapsTrimmed) w.push('Se recortaron fortalezas o brechas a un máximo de 2.');
  if (k.unavailableSources > 0) w.push(`Se quitaron ${k.unavailableSources} ítems de evidencia que citaban una fuente que no llegó.`);
  if (k.absenceAsEvidence > 0) w.push(`Se quitaron ${k.absenceAsEvidence} ítems que tomaban la falta de un dato como evidencia.`);
  if (k.levelsWithoutEvidence?.length) w.push(`Quedaron sin nivel por falta de evidencia: ${list(k.levelsWithoutEvidence)}.`);
  if (k.unavailableSignals > 0) w.push(`Se quitaron ${k.unavailableSignals} señales de la rueda del volumen sin datos que las respalden.`);
  if (k.volumeWheelDropped) w.push('Se quitó la rueda del volumen: quedaban menos de 3 señales con datos.');
  if (k.patternsWithoutEvidence > 0) w.push(`Se quitaron ${k.patternsWithoutEvidence} patrones sin evidencia de una fuente disponible.`);
  if (k.prioritiesTrimmed) w.push('Se recortaron las prioridades a 3.');
  if (checks.keptAfterRetry) w.push('La respuesta seguía mayormente en inglés después del reintento; se guardó igual para que la revises.');
  if (checks.rubricTextAsFact > 0) w.push(`Copia ${checks.rubricTextAsFact} tramos del texto de la rúbrica como si fueran hechos del cliente.`);
  if (checks.summaryWithoutNumber) w.push('El resumen no tiene ningún número concreto.');
  if (checks.summaryWithoutReferent) w.push('El resumen no nombra a la persona referente del equipo (la que tiene más aptitudes en Fortaleza).');
  if (checks.gapsMissingFromFocus?.length) w.push(`Aptitudes en Brecha a formar que no están en "En qué enfocarte": ${checks.gapsMissingFromFocus.join(', ')}.`);
  if (checks.focusOutOfOrder) w.push('"En qué enfocarte" no sigue el orden de las aptitudes en Brecha a formar (required_focus).');
  if (checks.focusNotAllowed?.length) w.push(`Aptitudes en "En qué enfocarte" que no corresponden (no están en Brecha a formar ni en Suficiente con lugar libre): ${checks.focusNotAllowed.join(', ')}.`);
  if (checks.fillerPhrases > 0) w.push(`Hay ${checks.fillerPhrases} frases de relleno (por ejemplo, "Espero que este análisis te sea útil").`);
  if (checks.drainZoneInFocus?.length) w.push(`Aptitudes en zona de drenaje dentro de "En qué enfocarte": ${checks.drainZoneInFocus.join(', ')}.`);
  if (checks.unsupportedChannels?.length) w.push(`Menciona canales que no son el validado ni aparecen en la evidencia: ${checks.unsupportedChannels.join(', ')}.`);
  if (checks.channelConflicts?.length) w.push(`Empuja algo que contradice las zonas o el canal: ${checks.channelConflicts.join(', ')}.`);
  if (checks.validatedChannelIsHard) w.push('El canal validado es uno que, según su autoevaluación, a la persona le cuesta.');
  if (checks.unsupportedNumbers > 0) w.push(`Tiene ${checks.unsupportedNumbers} ${checks.unsupportedNumbers === 1 ? 'número que no calculó' : 'números que no calculó'} el código ni están en los datos (dato inventado). Bórralo o reemplázalo por "Falta: preguntar en el taller…".`);
  if (checks.faltaForKnownData?.length) w.push(`Dice "Falta" sobre datos que sí están cargados (${checks.faltaForKnownData.map(k => ({ goal: 'la meta', stageMap: 'las etapas de reunión y oportunidad', contactRate: 'la conversión de contacto a reunión', objection: 'la objeción del role-play', openOpportunities: 'las oportunidades abiertas' })[k] || k).join(', ')}).`);
  if (checks.genericProof > 0) w.push(`Usa ${checks.genericProof === 1 ? 'una prueba genérica' : `${checks.genericProof} pruebas genéricas`} ("caso de éxito", "testimonio") sin un caso real en los datos: tiene que decir "Falta: un caso real para usar en el seguimiento".`);
  if (checks.missingNotFlagged) w.push('El código marcó datos que faltan y el borrador nunca escribe "Falta:": puede haber completado algo que no sabe.');
  if (checks.secondPerson > 0) w.push(`La versión founder le habla a la persona en segunda persona (${checks.secondPerson} palabras como "tú", "te", "enfócate"). Tiene que ir en tercera persona, con acciones para el founder o el manager.`);
  return w;
}

const MOTIVATOR_REASONS = {
  included: 'Incluidos: la persona dio consentimiento con nombre (solo los nombres de su top 3).',
  no_consent_in_form: 'No incluidos: en el formulario no aceptó que sus motivadores aparezcan con su nombre.',
  withdrawn_in_1on1: 'No incluidos: en la 1:1 quedó registrado "No (o ya no)".',
  no_consent: 'No incluidos: el consentimiento final (Motivators Named (Final)) es 0.',
  no_parte_3: 'No incluidos: hay consentimiento, pero la Parte 3 no está enviada.',
};

function Checks({ checks, refs, step, variant }) {
  const warnings = checkWarnings(checks);
  return (
    <>
      {warnings.length > 0 && (
        <div style={{ ...warnSt, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Revisar antes de usar</div>
          {warnings.map((x, i) => <div key={i}>• {x}</div>)}
        </div>
      )}
      {checks?.evidenceDroppedAsVerbatim > 0 && (
        <div style={{ ...mutedSt, marginBottom: 10 }}>Se descartaron {checks.evidenceDroppedAsVerbatim} ítems de evidencia que repetían frases de la 1:1.</div>
      )}
      {checks?.retriedWithFeedback > 0 && (
        <div style={{ ...mutedSt, marginBottom: 10 }}>Se reintentó una vez pidiéndole a la IA que corrigiera {checks.retriedWithFeedback} problemas de los controles automáticos.</div>
      )}
      {checks?.motivatorQuotesRemoved > 0 && (
        <div style={{ ...mutedSt, marginBottom: 10 }}>Se quitaron {checks.motivatorQuotesRemoved} citas de motivadores que no aparecen en la entrevista ni en las respuestas abiertas.</div>
      )}
      {step === 'P1' && refs && (
        <div style={{ ...mutedSt, marginBottom: 10 }}>
          Fuentes: role-play {refs.roleplay === 'recorded' ? 'con datos escondidos registrados' : refs.roleplay === 'not_recorded' ? 'sin datos escondidos registrados (no cuentan como evidencia)' : '—'} ·
          CRM {refs.crm ? 'con métricas de esta persona' : 'sin métricas vinculadas a esta persona (revisa el vínculo de vendedores en CRM)'}
        </div>
      )}
      {step === 'P3' && variant === 'founder' && refs?.motivators && (
        <div style={{ ...mutedSt, marginBottom: 10 }}>Motivadores: {MOTIVATOR_REASONS[refs.motivators] || refs.motivators}</div>
      )}
    </>
  );
}

const energyOf = (v) => ENERGY_OPTIONS.find(e => e.key === v);
const labelOf = (list, key) => t(list.find(x => x.key === key)?.label) || key;

function Evidence({ content }) {
  if (!content) return null;
  return (
    <div>
      {(content.aptitudes || []).map(a => {
        const e = energyOf(a.energy?.value);
        return (
          <div key={a.aptitude} style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{t(APTITUDES.find(x => x.key === a.aptitude)?.label) || a.aptitude}
              {e && <span style={{ ...mutedSt, fontWeight: 400 }}> · {e.symbol} {t(e.label)}{a.energy.quote ? ` — "${a.energy.quote}"` : ''}</span>}</div>
            {a.evidence.length ? a.evidence.map((x, i) => (
              <div key={i} style={{ fontSize: 13, marginLeft: 10 }}>
                {x.direction === 'up' ? '▲' : '▼'} <span style={mutedSt}>{SOURCE_LABELS[x.source] || x.source}:</span> {x.quote_or_observation}
              </div>
            )) : <div style={{ ...mutedSt, marginLeft: 10 }}>Sin evidencia.</div>}
          </div>
        );
      })}
      <div style={{ fontSize: 13, marginBottom: 10 }}><b>Canal donde rinde más:</b> {content.best_channel?.channel ? labelOf(CHANNELS, content.best_channel.channel) : '—'} <span style={mutedSt}>{content.best_channel?.evidence}</span></div>
      <div style={{ ...warnSt, fontSize: 13 }}>
        <div style={{ ...labelSt, marginBottom: 6 }}>Confidencial: solo para Ale</div>
        <div><b>Motivadores:</b> {(content.motivators || []).map(m => `${labelOf(MOTIVATORS, m.motivator)}${m.quote ? ` ("${m.quote}")` : ''}`).join(' · ') || '—'}</div>
        <div><b>Cansancio o frustración general (no se asigna a ninguna aptitud):</b> {(content.general_signals || []).join(' · ') || '—'}</div>
        <div><b>Qué necesita para quedarse (motivación, no riesgo):</b> {(content.stay_conditions || []).join(' · ') || '—'}</div>
        <div><b>Señales de riesgo de salida:</b> {(content.exit_risk_signals || []).join(' · ') || '—'}</div>
      </div>
      <div style={{ fontSize: 13 }}><b>Contradicciones:</b> {(content.contradictions || []).join(' · ') || '—'}</div>
    </div>
  );
}
